import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import ReactToPrint from 'react-to-print';
import {
  IconQrcode,
  IconSearch,
  IconX,
  IconClock,
  IconTrash,
  IconPrinter,
  IconTag,
  IconWeight,
  IconCoins,
  IconUser,
  IconCalendar,
  IconBuilding,
  IconReceipt,
  IconRefresh,
  IconAlertCircle,
  IconCircleCheck,
  IconPhoto,
  IconKeyboard,
  IconCamera,
} from '@tabler/icons-react';
import QrScanner from 'qr-scanner';
import { getAuthorizationHeader, http, imageBaseUrl } from '../config/http';
import Layouts from '../layouts/Layouts';
import { DinamicTitle } from '../utils/DinamicTitle';
import PrintSuratTrxPenjualan from './Transaksi/PrintSuratTrxPenjualan';
import TestingPrint from './Transaksi/TestingPrint';
import SuratKeruak from './Transaksi/SuratKeruak';
import SuratToko from '../components/surat/SuratToko';

function numberWithCommas(x) {
  if (!x && x !== 0) return '0';
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// ─── Scanner Modal ───────────────────────────────────────────────────────────
function ScannerModal({ isOpen, onClose, onScanSuccess }) {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const lastScanRef = useRef(0);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [manualInput, setManualInput] = useState('');

  const startScanner = async () => {
    if (!videoRef.current) return;
    try {
      scannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          const now = Date.now();
          if (now - lastScanRef.current < 1500) return;
          lastScanRef.current = now;
          new Audio('/scan-beep.mp3').play().catch(() => {});
          destroyScanner();
          onScanSuccess(result.data);
          onClose();
        },
        {
          preferredCamera: 'environment',
          highlightScanRegion: true,
          highlightCodeOutline: true,
        }
      );
      await scannerRef.current.start();
      setIsScanning(true);
      setScanError('');
    } catch {
      setScanError('Gagal mengakses kamera. Pastikan izin kamera diberikan.');
      setIsScanning(false);
    }
  };

  const destroyScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop();
      scannerRef.current.destroy();
      scannerRef.current = null;
      setIsScanning(false);
    }
  };

  const handleOpen = () => {
    if (!showManual) startScanner();
  };

  const handleClose = () => {
    destroyScanner();
    setShowManual(false);
    setManualInput('');
    onClose();
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      onScanSuccess(manualInput.trim());
      setManualInput('');
      setShowManual(false);
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}
      afterEnter={handleOpen}
      beforeLeave={destroyScanner}
    >
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border dark:border-slate-700/60 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <Dialog.Title className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  <IconQrcode className="w-5 h-5 text-amber-500" />
                  Scan QR Kwitansi
                </Dialog.Title>
                <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
                  <IconX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5">
                {!showManual ? (
                  <>
                    <div className="relative rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800" style={{ aspectRatio: '1' }}>
                      <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
                      {isScanning && (
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                          <div className="w-52 h-52 relative">
                            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-amber-500 rounded-tl-lg" />
                            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-amber-500 rounded-tr-lg" />
                            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-amber-500 rounded-bl-lg" />
                            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-amber-500 rounded-br-lg" />
                          </div>
                        </div>
                      )}
                    </div>
                    {scanError && (
                      <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
                        {scanError}
                      </div>
                    )}
                    <button onClick={() => { destroyScanner(); setShowManual(true); }} className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 font-medium text-sm transition-colors">
                      <IconKeyboard className="w-4 h-4" /> Input Manual
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">Masukkan kode faktur dari kwitansi penjualan</p>
                    <form onSubmit={handleManualSubmit} className="space-y-3">
                      <input
                        type="text"
                        value={manualInput}
                        onChange={(e) => setManualInput(e.target.value)}
                        placeholder="Contoh: TRX-20240101-001"
                        autoFocus
                        className="w-full h-11 px-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 transition-all placeholder-slate-400"
                      />
                      <div className="flex gap-2">
                        <button type="button" onClick={() => { setShowManual(false); setTimeout(startScanner, 100); }} className="flex-1 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium transition-colors flex items-center justify-center gap-1.5">
                          <IconCamera className="w-4 h-4" /> Kamera
                        </button>
                        <button type="submit" disabled={!manualInput.trim()} className="flex-1 h-10 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-bold transition-colors">
                          Cari
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
              <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
                <p className="text-xs text-center text-slate-400 dark:text-slate-500">
                  Arahkan ke QR Code / Barcode pada kwitansi penjualan
                </p>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

// ─── Result Card ─────────────────────────────────────────────────────────────
function ImagePreviewOverlay({ src, onClose }) {
  const [scale, setScale] = useState(1);

  const zoomIn  = () => setScale((s) => Math.min(s + 0.5, 5));
  const zoomOut = () => setScale((s) => Math.max(s - 0.5, 0.5));
  const reset   = () => setScale(1);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 flex flex-col"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Image area */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <img
          src={src}
          alt="preview"
          style={{ transform: `scale(${scale})`, transformOrigin: 'center', transition: 'transform 0.2s ease' }}
          className="max-w-none rounded-lg shadow-2xl"
          draggable={false}
        />
      </div>

      {/* Bottom toolbar */}
      <div className="flex-shrink-0 flex items-center justify-center gap-4 py-4 bg-black/60 backdrop-blur-sm">
        <button onClick={zoomOut} className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 text-white flex items-center justify-center transition-all" title="Zoom out">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
        <button onClick={reset} className="px-4 h-11 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 text-white text-sm font-bold tracking-wide transition-all min-w-[64px]">
          {Math.round(scale * 100)}%
        </button>
        <button onClick={zoomIn} className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 text-white flex items-center justify-center transition-all" title="Zoom in">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
        <div className="w-px h-6 bg-white/20 mx-1" />
        <button onClick={onClose} className="w-11 h-11 rounded-full bg-red-500/70 hover:bg-red-500 active:scale-95 text-white flex items-center justify-center transition-all" title="Tutup">
          <IconX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function ResultCard({ trx, onClose, onPrint, printRef }) {
  const tipe_surat = localStorage.getItem('tipe_surat');
  const [imgError, setImgError] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [gdriveModal, setGdriveModal] = useState({ open: false, loading: false, imageUrl: null, viewUrl: null, error: null });
  const [gdriveScale, setGdriveScale] = useState(1);

  // gambar1 dari BE berupa JSON string "[{asli,thumbnail}]" — perlu di-parse dulu
  const g1Raw = trx?.item?.gambar1;
  let gambar1Obj = null;
  try {
    if (Array.isArray(g1Raw))                      gambar1Obj = g1Raw[0];
    else if (g1Raw && typeof g1Raw === 'object')   gambar1Obj = g1Raw;
    else if (typeof g1Raw === 'string') {
      const parsed = JSON.parse(g1Raw);
      gambar1Obj = Array.isArray(parsed) ? parsed[0] : parsed;
    }
  } catch { gambar1Obj = null; }
  const imgSrc     = gambar1Obj?.asli
    ? `${imageBaseUrl}${gambar1Obj.asli}`
    : gambar1Obj?.thumbnail
      ? `${imageBaseUrl}${gambar1Obj.thumbnail}`
      : null;

  // Reset semua state saat trx berubah (scan baru)
  useEffect(() => {
    setImgError(false);
    setPreviewOpen(false);
    setGdriveModal({ open: false, loading: false, imageUrl: null, viewUrl: null, error: null });
    setGdriveScale(1);
  }, [trx?.id]);

  async function handleLihatFoto(folderIdOverride) {
    const fromGdrive  = trx?.gdrive?.id_unique;
    const fromEnv     = process.env.REACT_APP_GDRIVE_FOLDER_ID_UNIQUE_DEFAULT;
    const folderId    = folderIdOverride ?? fromGdrive ?? fromEnv;
    const fileName    = `${trx?.item?.id}.png`;

    console.log('[handleLihatFoto]', { folderIdOverride, fromGdrive, fromEnv, folderId, fileName });

    if (!folderId) {
      toast.error(`Folder ID tidak tersedia. gdrive: ${fromGdrive ?? 'null'}, env default: ${fromEnv ?? 'tidak di-set (restart dev server)'}`, { autoClose: 4000 });
      return;
    }

    setGdriveModal({ open: true, loading: true, imageUrl: null, viewUrl: null, error: null });

    const apiKey = process.env.REACT_APP_GDRIVE_API_KEY;
    if (!apiKey) {
      setGdriveModal({ open: true, loading: false, imageUrl: null, viewUrl: null, error: 'API Key Google Drive belum dikonfigurasi. Restart dev server setelah mengisi .env.' });
      return;
    }
    const query  = `name='${fileName}' and '${folderId}' in parents and trashed=false`;
    const url    = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,mimeType)&key=${apiKey}`;

    try {
      const res  = await fetch(url);
      const data = await res.json();

      if (data.files && data.files.length > 0) {
        const fileId = data.files[0].id;
        setGdriveModal({
          open: true, loading: false,
          imageUrl: `https://lh3.googleusercontent.com/d/${fileId}=s1000`,
          viewUrl:  `https://drive.google.com/file/d/${fileId}/view`,
          error: null,
        });
      } else {
        setGdriveModal({ open: true, loading: false, imageUrl: null, viewUrl: null, error: 'File tidak ditemukan di Drive Backup Cadangan.' });
      }
    } catch {
      setGdriveModal({ open: true, loading: false, imageUrl: null, viewUrl: null, error: 'Gagal mengambil data. Periksa koneksi.' });
    }
  }

  const totalHarga   = trx?.total_harga ?? trx?.harga_jual ?? null;
  const berat         = parseFloat(trx?.item?.berat) || 0;
  const hargaPerGram  = totalHarga && berat ? Math.round(totalHarga / berat) : null;

  return (
    <>
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700/60 shadow-lg overflow-hidden">
      {/* Print template (hidden) */}
      <div ref={printRef} className="hidden print:block">
        {tipe_surat === '1' && <PrintSuratTrxPenjualan isOpen={true} detailPenjualan={trx} />}
        {tipe_surat === '2' && <TestingPrint isOpen={true} detailPenjualan={trx} isTertunda={false} />}
        {tipe_surat === '3' && <SuratKeruak isOpen={true} detailPenjualan={trx} />}
        {tipe_surat === '4' && <SuratToko isOpen={true} detailPenjualan={trx} isTertunda={false} />}
      </div>

      {/* Status Banner */}
      <div className={`px-5 py-3 flex items-center justify-between ${trx.is_refund ? 'bg-red-50 dark:bg-red-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20'}`}>
        <div className="flex items-center gap-2">
          {trx.is_refund ? (
            <>
              <IconAlertCircle className="w-5 h-5 text-red-500" />
              <span className="font-bold text-red-700 dark:text-red-400 text-sm">BARANG DIKEMBALIKAN</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm4.47 7.28-5 6a1 1 0 0 1-.75.36 1 1 0 0 1-.74-.33l-2.5-2.75a1 1 0 0 1 1.49-1.34l1.72 1.9 4.27-5.12a1 1 0 1 1 1.51 1.28z"/></svg>
              <span className="font-bold text-emerald-700 dark:text-emerald-400 text-sm">TRANSAKSI VALID</span>
            </>
          )}
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-slate-400 transition-colors">
          <IconX className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 grid gap-5">
        {/* Kode Faktur & Tanggal */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pb-2 border-b border-slate-100 dark:border-slate-800">
          <div>
            {/* <p className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide font-medium">Kode Faktur</p> */}
            <p className="text-lg font-extrabold text-amber-600 dark:text-amber-400 tracking-tight mt-0.5">{trx.kode_trx}</p>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-1.5">
            <IconCalendar className="w-4 h-4 text-amber-500" />
            <span className="font-medium">{trx.created_at}</span>
          </div>
        </div>

        {/* Item Info + Photo */}
        <div className="flex gap-4">
          {/* Photo */}
          <div className="flex-shrink-0 w-32 h-35 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            {imgSrc && !imgError ? (
              <button onClick={() => setPreviewOpen(true)} className="w-full h-full block relative group">
                <img
                  src={imgSrc}
                  alt="item"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  onError={() => setImgError(true)}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <IconPhoto className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                </div>
              </button>
            ) : (
              <button
                onClick={() => handleLihatFoto()}
                className="w-full h-full flex flex-col items-center justify-center gap-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-amber-400 dark:hover:border-amber-600 transition-all group rounded-xl"
              >
                <IconPhoto className="w-7 h-7 text-slate-300 dark:text-slate-600 group-hover:text-amber-400 transition-colors" />
                <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 group-hover:text-amber-500 transition-colors leading-tight text-center px-1">
                  Lihat<br />Foto
                </span>
              </button>
            )}
          </div>

          {/* Full Preview Overlay */}
          {previewOpen && imgSrc && (
            <ImagePreviewOverlay src={imgSrc} onClose={() => setPreviewOpen(false)} />
          )}

          {/* Item Details */}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-500 uppercase tracking-wide font-medium mb-1">
              Waktu Input: {trx.item?.created_at ? trx.item.created_at : '-'}
            </p>
            <p className="font-bold text-slate-800 dark:text-slate-100 text-base leading-tight">{trx.item?.jenis_kategori?.nama_jenis || '-'}</p>
            <p className="text-bold text-md text-amber-600 dark:text-amber-400 mt-0.5">{trx.item?.kode_item}</p>
            <div className="flex flex-wrap gap-2 mt-0.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-md text-sm font-semibold">
                <IconWeight className="w-3.5 h-3.5" /> {trx.item?.berat} gr
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-md text-sm font-semibold">
                <IconCoins className="w-3.5 h-3.5" /> {trx.item?.karat} karat
              </span>
            </div>
            {trx.item?.harga_modal ? (
              <div className="mt-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-md text-sm font-bold">
                  <IconTag className="w-3.5 h-3.5" /> Modal: Rp {numberWithCommas(trx.item.harga_modal)}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Google Drive links — tampil hanya jika gambar tidak tersedia (sama kondisi Lihat Foto) */}
        {(!imgSrc || imgError) && (
          <div className={`grid gap-3 ${process.env.REACT_APP_GDRIVE_FOLDER_ID_UNIQUE_DEFAULT ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {/* Tombol kiri: folder spesifik per transaksi */}
            {trx.gdrive ? (
              <button
                onClick={() => handleLihatFoto()}
                className="rounded-xl px-3 py-3 text-sm flex flex-col items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 dark:bg-amber-500 dark:hover:bg-amber-600 active:scale-[0.98] transition-all shadow-sm shadow-amber-500/30"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.28 3L1 12.5 6.28 22h11.44L23 12.5 17.72 3H6.28zm5.72 14.5a5 5 0 110-10 5 5 0 010 10z"/>
                </svg>
                <span className="font-bold text-white text-xs leading-tight text-center">Gambar Drive<br/><span className="font-semibold opacity-90">{trx.gdrive.label || `${trx.gdrive.nama_bulan} ${trx.gdrive.tahun}`}</span></span>
              </button>
            ) : (
              <div className="rounded-xl px-3 py-3 text-sm flex flex-col items-center justify-center gap-1.5 bg-amber-100 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 opacity-60">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.28 3L1 12.5 6.28 22h11.44L23 12.5 17.72 3H6.28zm5.72 14.5a5 5 0 110-10 5 5 0 010 10z"/>
                </svg>
                <span className="font-medium text-amber-700 dark:text-amber-400 text-xs text-center">Gambar Drive<br/><span className="italic font-normal text-[10px]">Tidak tersedia</span></span>
              </div>
            )}

            {/* Tombol kanan: folder default dari env */}
            {process.env.REACT_APP_GDRIVE_FOLDER_ID_UNIQUE_DEFAULT && (
              <button
                onClick={() => handleLihatFoto(process.env.REACT_APP_GDRIVE_FOLDER_ID_UNIQUE_DEFAULT)}
                className="rounded-xl px-3 py-3 text-sm flex flex-col items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 dark:bg-amber-500 dark:hover:bg-amber-600 active:scale-[0.98] transition-all shadow-sm shadow-amber-500/30"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.28 3L1 12.5 6.28 22h11.44L23 12.5 17.72 3H6.28zm5.72 14.5a5 5 0 110-10 5 5 0 010 10z"/>
                </svg>
                <span className="font-bold text-white text-xs leading-tight text-center">Gambar Drive<br/><span className="font-semibold opacity-90">(folder opsional)</span></span>
              </button>
            )}
          </div>
        )}

        {/* Transaction Details Grid */}
        <div className="grid gap-2">
          {[
            { label: 'Harga Jual / gram', value: hargaPerGram ? `Rp ${numberWithCommas(hargaPerGram)}` : '-', icon: IconTag },
            { label: 'Total Harga', value: totalHarga ? `Rp ${numberWithCommas(totalHarga)}` : '-', icon: IconReceipt, bold: true, highlight: true },
            { label: 'Kasir Pembuat Surat', value: trx.user?.name || '-', icon: IconUser },
            { label: 'Cabang Pembuat Surat', value: trx.item?.cabang?.nama_cabang || trx.user?.cabang?.nama_cabang || '-', icon: IconBuilding },
          ].map(({ label, value, icon: Icon, bold, highlight }) => (
            <div key={label} className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-sm ${highlight ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-700/30' : 'bg-slate-50 dark:bg-slate-800/50'}`}>
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <Icon className="w-4 h-4" />
                <span className="font-medium">{label}</span>
              </div>
              <span className={`${bold ? 'text-base font-extrabold text-amber-600 dark:text-amber-400' : 'font-semibold text-slate-700 dark:text-slate-300'}`}>{value}</span>
            </div>
          ))}

          {/* Laba */}
          {trx.laba !== null && (
            <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-sm border ${
              trx.laba >= 0
                ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200/60 dark:border-emerald-700/30'
                : 'bg-red-50 dark:bg-red-900/10 border-red-200/60 dark:border-red-700/30'
            }`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${trx.laba >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <span className={`font-semibold text-sm ${trx.laba >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                  {trx.laba >= 0 ? 'Untung' : 'Rugi'}
                </span>
              </div>
              <span className={`font-bold text-sm ${trx.laba >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                {trx.laba < 0 ? '- ' : '+ '}Rp {numberWithCommas(Math.abs(Math.round(trx.laba)))}
              </span>
            </div>
          )}
        </div>

        {/* Tanggal Input Barang */}
        {/* <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
          <span className="font-medium">Barang diinput: </span>
          <span className="font-semibold text-slate-700 dark:text-slate-300">
            {trx.item?.created_at ? String(trx.item.created_at).split(' ')[0] : '-'}
          </span>
        </div> */}

        {/* Actions */}
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-semibold text-sm transition-colors">
            Tutup
          </button>
          <ReactToPrint
            trigger={() => (
              <button className="flex-1 h-10 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-sm shadow-amber-500/30 transition-all">
                <IconPrinter className="w-4 h-4" /> Cetak Ulang
              </button>
            )}
            content={() => printRef.current}
          />
        </div>
      </div>
    </div>

    {/* ── GDrive Photo Preview Modal ───────────────────────── */}

    {gdriveModal.open && (
      <div className="fixed inset-0 z-[9999] bg-black/90 flex flex-col" onClick={(e) => { if (e.target === e.currentTarget) setGdriveModal((s) => ({ ...s, open: false })); }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-black/60">
          <span className="text-white text-sm font-semibold">Foto dari Drive Backup Cadangan</span>
          <button onClick={() => setGdriveModal((s) => ({ ...s, open: false }))} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors">
            <IconX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
          {gdriveModal.loading && (
            <div className="flex flex-col items-center gap-3 text-white/60">
              <svg className="w-8 h-8 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              <span className="text-sm">Mencari file di Drive Backup Cadangan ...</span>
            </div>
          )}

          {!gdriveModal.loading && gdriveModal.error && (
            <div className="text-center text-white/70 max-w-xs">
              <IconPhoto className="w-12 h-12 mx-auto mb-3 text-white/30" />
              <p className="text-sm font-medium">{gdriveModal.error}</p>
              {gdriveModal.viewUrl && (
                <a href={gdriveModal.viewUrl} target="_blank" rel="noreferrer" className="mt-3 inline-block px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition-colors">
                  Buka di Google Drive
                </a>
              )}
            </div>
          )}

          {!gdriveModal.loading && gdriveModal.imageUrl && (
            <div className="overflow-auto flex items-center justify-center w-full h-full">
              <img
                src={gdriveModal.imageUrl}
                alt="foto gdrive"
                style={{ transform: `scale(${gdriveScale})`, transformOrigin: 'center', transition: 'transform 0.2s ease' }}
                className="max-w-none rounded-lg shadow-2xl"
                referrerPolicy="no-referrer"
                draggable={false}
                onError={() => setGdriveModal((s) => ({ ...s, imageUrl: null, error: 'Gagal menampilkan gambar. Silahkan reload atau klik ulang tombol Lihat Foto.' }))}
              />
            </div>
          )}
        </div>

        {/* Bottom toolbar — zoom + close */}
        {!gdriveModal.loading && (gdriveModal.imageUrl || gdriveModal.viewUrl) && (
          <div className="flex-shrink-0 flex items-center justify-center gap-4 py-4 bg-black/60 backdrop-blur-sm">
            {gdriveModal.imageUrl && (
              <>
                <button onClick={() => setGdriveScale((s) => Math.max(s - 0.5, 0.5))} className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 text-white flex items-center justify-center transition-all" title="Zoom out">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
                <button onClick={() => setGdriveScale(1)} className="px-4 h-11 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 text-white text-sm font-bold tracking-wide transition-all min-w-[64px]">
                  {Math.round(gdriveScale * 100)}%
                </button>
                <button onClick={() => setGdriveScale((s) => Math.min(s + 0.5, 5))} className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 text-white flex items-center justify-center transition-all" title="Zoom in">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
                <div className="w-px h-6 bg-white/20 mx-1" />
              </>
            )}
            {gdriveModal.viewUrl && (
              <a href={gdriveModal.viewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 h-11 rounded-full bg-blue-600/80 hover:bg-blue-500 text-white text-xs font-semibold transition-colors">
                <IconPhoto className="w-4 h-4" /> Drive
              </a>
            )}
            <div className="w-px h-6 bg-white/20 mx-1" />
            <button onClick={() => setGdriveModal((s) => ({ ...s, open: false }))} className="w-11 h-11 rounded-full bg-red-500/70 hover:bg-red-500 active:scale-95 text-white flex items-center justify-center transition-all" title="Tutup">
              <IconX className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ScanKwitansi() {
  DinamicTitle('Scan Kwitansi || Komaladewi');
  const navigate = useNavigate();

  const [scannerOpen, setScannerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const printRef = useRef();
  const searchInputRef = useRef();

  useEffect(() => { searchInputRef.current?.focus(); }, []);

  async function lookupTransaction(kode) {
    if (!kode?.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await http.get(`/penjualan/findfaktur/${kode.trim()}`, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      const trx = res.data?.data;
      if (!trx) {
        toast.error('Kwitansi tidak ditemukan. Periksa kode faktur.', { autoClose: 2500 });
        return;
      }
      setResult(trx);
      // Add to history (avoid duplicates)
      setHistory((prev) => {
        const exists = prev.find((h) => h.kode_trx === trx.kode_trx);
        if (exists) return prev;
        return [{ ...trx, scanned_at: new Date().toLocaleTimeString('id-ID') }, ...prev].slice(0, 20);
      });
      toast.success('Kwitansi ditemukan!', { autoClose: 1000 });
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 404) {
        toast.error('Kwitansi tidak ditemukan', { autoClose: 2000 });
      } else {
        toast.error('Gagal mengambil data. Coba lagi.', { autoClose: 2000 });
      }
    } finally {
      setLoading(false);
    }
  }

  const handleSearch = (e) => {
    e.preventDefault();
    lookupTransaction(searchInput);
  };

  return (
    <Layouts>
      <div className="p-4 sm:p-6">
        {/* Header */}
        {/* <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Scan Kwitansi</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Scan QR Code pada kwitansi penjualan untuk melihat detail transaksi
          </p>
        </div> */}

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-6 items-start">

          {/* ── Left: Scan controls + Result ── */}
          <div className="w-full lg:flex-1 min-w-0">
            {/* Scan Button */}
            {/* <button
              onClick={() => setScannerOpen(true)}
              disabled={loading}
              className="w-full py-3 mb-4 rounded-2xl bg-amber-500 hover:bg-amber-600 active:scale-[0.99] text-white font-bold text-base flex flex-col items-center justify-center gap-2 shadow-lg shadow-amber-500/25 transition-all disabled:opacity-70"
            >
              {loading ? (
                <>
                  <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Mencari data...</span>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-1">
                    <IconQrcode className="w-8 h-8" />
                  </div>
                  <span>Tap untuk Scan QR Code</span>
                  <span className="text-amber-100/80 text-xs font-normal">Jika tidak bisa scan, coba manual input (Kode Barang atau Kode Faktur)</span>
                </>
              )}
            </button> */}

            {/* Manual Search */}
            <form onSubmit={handleSearch} className="flex gap-2 mb-6">
              <div className="relative flex-1">
                <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value); if (!e.target.value) setResult(null); }}
                  placeholder="Masukkan kode barang atau kode faktur..."
                  className="w-full h-14 pl-11 pr-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-base font-medium focus:outline-none focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 transition-all placeholder-slate-400"
                />
              </div>
              <button type="submit" disabled={loading || !searchInput.trim()} className="h-14 px-6 rounded-xl bg-slate-800 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold text-base transition-colors disabled:opacity-50">
                Cari
              </button>
            </form>

            {/* Result */}
            {result ? (
              <ResultCard
                trx={result}
                printRef={printRef}
                onClose={() => {
                  setResult(null);
                  setSearchInput('');
                  setTimeout(() => searchInputRef.current?.focus(), 50);
                }}
                onPrint={() => {}}
              />
            ) : !loading && history.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-slate-300 dark:text-slate-600">
                <IconQrcode className="w-16 h-16 mb-4 opacity-50" />
                <p className="font-semibold text-slate-400 dark:text-slate-500">Belum ada scan</p>
                <p className="text-sm text-slate-400 dark:text-slate-600 mt-1">Tap tombol di atas untuk mulai scan</p>
              </div>
            )}
          </div>

          {/* ── Right: Riwayat Scan ── */}
          {history.length > 0 && (
            <div className="w-full lg:w-96 xl:w-[26rem] flex-shrink-0">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/60 rounded-2xl shadow-sm overflow-hidden">
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60">
                  <div className="flex items-center gap-2.5">
                    <IconClock className="w-5 h-5 text-amber-500" />
                    <span className="font-bold text-base text-slate-800 dark:text-slate-100">Riwayat Scan</span>
                    <span className="text-xs bg-amber-500 text-white rounded-full px-2.5 py-0.5 font-bold">{history.length}</span>
                  </div>
                  <button
                    onClick={() => {
                      setHistory([]);
                      setResult(null);
                      setSearchInput('');
                      setTimeout(() => searchInputRef.current?.focus(), 50);
                    }}
                    className="flex items-center gap-1.5 text-xs text-amber-600 font-medium transition-colors"
                  >
                    <IconRefresh className="w-3.5 h-3.5" /> Refresh Semua
                  </button>
                </div>
                {/* Card list */}
                <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[calc(100vh-280px)] overflow-y-auto">
                  {history.map((h, i) => (
                    <button
                      key={i}
                      onClick={() => setResult(h)}
                      className="w-full text-left px-4 py-3 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors group"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                          <IconReceipt className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-amber-600 dark:text-amber-400 text-sm font-mono leading-tight truncate">{h.kode_trx}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                            {h.item?.jenis_kategori?.nama_jenis} · {h.item?.berat} gr
                          </p>
                        </div>
                        <div className="flex flex-col items-end flex-shrink-0">
                          <span className="text-sm font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                            Rp {numberWithCommas(h.total_harga || h.harga_jual || 0)}
                          </span>
                          <span className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{h.scanned_at}</span>
                        </div>
                      </div>
                      {h.is_refund && (
                        <span className="mt-1.5 inline-block text-[9px] font-bold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded px-1.5 py-0.5">
                          DIKEMBALIKAN
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Scanner Modal */}
      <ScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanSuccess={(kode) => lookupTransaction(kode)}
      />
    </Layouts>
  );
}
